<script setup lang="ts">
import { computed, inject, reactive, ref, toRaw, watch } from 'vue';
import QueryEditor from './QueryEditor.vue';
import {
  legacyModeKey,
  Models,
  useContext,
  useDialogs,
  useEnums,
  useGraphMeta,
  useLocalization,
  useProcesses
} from '@hantera/portal-app';
import {
  ContextMenuItem,
  ContextMenuSection,
  ContextMenuSections,
  type DataColumn,
  type DataRow,
  DataTable,
  type DataTableContextMenuProvider,
  DropdownButton,
  DropdownItem,
  Icon,
  IconReload,
  Panel,
  TextInput,
  ToggleButton
} from '@hantera/design-system';
import { Query, QueryNavigation } from '@/backend/queries';
import useBackend from '@/composition/useBackend';
import cleanAssign from '@/clean-assign';
import QueryDataProvider from "./query-data-provider";
import useGraphPhrases from "@/composition/use-graph-phrases";
import buildQuery from "./use-query-builder";
import QueryExport from "./QueryExport.vue";
import ColumnRenameDialog from "./ColumnRenameDialog.vue";
import SaveAsDialog from "./SaveAsDialog.vue";

const legacyMode = inject(legacyModeKey, false)

// TODO The registered viewers will be fetched from app context. It's hard coded for now. / Kristoffer 2025-05-26
const registeredNodeViews: {
  node: string
  name: string
  path: string
}[] = [{
  node: 'order',
  name: 'Order',
  path: legacyMode ? '/legacy-order' : '/order'
}]

if (legacyMode && !!localStorage.getItem('allowModernOrderView')) {
  registeredNodeViews.push({
    node: 'order',
    name: 'Modern Order',
    path: '/order'
  })
}

const t = useLocalization();
const { lookup, queries } = useBackend();
const graphMeta = useGraphMeta();
const queryError = ref<string>();
const dialogs = useDialogs()
const context = useContext()

let query: Query = reactive({
  name: '',
  navigation: {},
  columns: [],
  folderId: ''
});

const queryId = computed(() => context.currentView.value?.query.id!)

const searchPhrase = ref(context.currentView.value?.state.searchPhrase ?? '')

if (context.currentView.value!.state.transientQuery) {
  cleanAssign(query, context.currentView.value!.state.transientQuery);
} else {

  const savedQuery = queries.queries[queryId.value];

  if (savedQuery === undefined) {
    // Query not found. Possibly deleted.
    context.getView(context.currentView.value!.viewId)!.close()
  } else {
    cleanAssign(query, savedQuery);
  }
}

const savedQuery = computed(() => {
  if (queryId.value) {
    return queries.queries[queryId.value];
  }
  return undefined;
});

let pauseWatch = false;

watch(savedQuery, () => {
  if (savedQuery.value !== undefined && context.currentView.value!.state.transientQuery === undefined) {
    pauseWatch = true;
    cleanAssign(query, savedQuery.value);
    pauseWatch = false;
  }
}, {
  deep: true
});

watch(query, () => {
  if (!pauseWatch) {
    if (context.currentView.value!.state.transientQuery === undefined) {
      context.currentView.value!.state.transientQuery = toRaw(query);
    } else {
      cleanAssign(context.currentView.value!.state.transientQuery, query);
    }
  }
}, {
  deep: true,
  // Flush=sync is necessary to allow pauseWatch to work
  flush: 'sync'
});

watch(() => savedQuery.value?.name, () => {
  context.currentView.value!.name.value = savedQuery.value?.name ?? t('workspace.newQuery');
}, {
  immediate: true
});

watch(queries.queries, () => {
  if (queryId.value && !queries.queries[queryId.value] && context.currentView.value!.state.transientQuery === undefined) {
    // Query not found anymore. Probably deleted.
    context.getView(context.currentView.value!.viewId)?.close()
  }
}, {
  deep: true
});

watch(() => context.currentView.value!.state.transientQuery, () => {
  context.currentView.value!.hasPendingChanges.value = typeof context.currentView.value!.state.transientQuery !== 'undefined';
  queryError.value = undefined;
}, {
  immediate: true,
  deep: true
});

watch(searchPhrase, () => {
  context.currentView.value!.state.searchPhrase = searchPhrase.value
})

const provider = new QueryDataProvider(
  query,
  context.currentView.value!.state.selections,
  useBackend().graph,
  useGraphMeta(),
  useProcesses(),
  useGraphPhrases(),
  useEnums(),
  searchPhrase
)

async function save() {
  if (!context.currentView.value!.state.transientQuery) {
    return;
  }

  if (!context.currentView.value!.state.transientQuery.navigation.edge) {
    queryError.value = 'Query must have a navigation';
    return;
  }

  if (context.currentView.value!.state.transientQuery.columns.length === 0) {
    queryError.value = 'Query must have at least one column';
    return;
  }

  if (context.currentView.value!.query.id === 'new') {

    await dialogs.openModal('Query Name', SaveAsDialog, true, {
      viewContext: context.currentView.value!
    })
  } else {
    const savedQuery = await queries.saveQuery(context.currentView.value!.state.transientQuery);

    // Since queries is bound to reactive query object, saving it will update this tab's state automatically.
    // We just have to clear the transient state:
    context.currentView.value!.state.transientQuery = undefined;
  }
}

const saveButtonLabel = computed(() => {
  if (context.currentView.value!.query.id === 'new') {
    return 'Save As..'
  } else {
    return 'Save'
  }
})

function exportTable(format: 'csv' | 'xlsx') {

  const rootEdges = Object.keys(graphMeta).reduce((res, nodeKey) => {
    const node = graphMeta[nodeKey];
    res[node.rootEdgeName] = {
      node: nodeKey,
      cardinality: 'many'
    };
    return res;
  }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>);

  const builtQuery = buildQuery(query, context.currentView.value!.state.selections, graphMeta, query.navigation, rootEdges, false, true);

  if (!builtQuery) {
    // Invalid query, ignore
    return;
  }

  if (searchPhrase.value) {
    builtQuery.phrase = searchPhrase.value
  }

  dialogs.openModal('Exporting', QueryExport, false, {
    queryName: query.name,
    query: builtQuery,
    format
  });
}

function refresh() {
  provider.refresh()
}

const nodeViews = computed(() => {

  const result: {
    node: string
    name: string
    idIdx: number
    path: string
  }[] = []

  if (!query.navigation.edge) {
    return result
  }

  const nodesByRootKey = Object.keys(graphMeta).reduce((res, nodeKey) => {
    const node = graphMeta[nodeKey];
    res[node.rootEdgeName] = node;
    return res;
  }, {} as Record<string, Models.GraphMeta.GraphNodeMeta>);

  const rootEdges = Object.keys(graphMeta).reduce((res, nodeKey) => {
    const node = graphMeta[nodeKey];
    res[node.rootEdgeName] = {
      node: nodeKey,
      cardinality: 'many'
    };
    return res;
  }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>)
  const fieldIndex: Record<string, number> = {};
  updateFieldIndex(query.navigation, rootEdges, fieldIndex)

  getViewer(query.navigation, nodesByRootKey[query.navigation.edge])

  function getViewer(navigation: QueryNavigation, node: Models.GraphMeta.GraphNodeMeta, queryPath?: string[]) {

    if (!queryPath) {
      queryPath = [navigation.edge!]
    }

    const idField = fieldIndex[queryPath.join('.') + '@' + node.idField]
    if (typeof idField !== 'undefined') {
      const viewers = registeredNodeViews.filter(r => r.node === node.name)
      for (const viewer of viewers) {
        result.push({
          name: viewer.name,
          node: node.name,
          idIdx: idField,
          path: viewer.path
        })
      }
    }

    if (!navigation.navigation) {
      return
    }

    for (const subNav of navigation.navigation) {

      if (!subNav.edge) { return }

      const subNodeName = node.edges[subNav.edge]?.node
      if (!subNodeName) { continue }

      const subNode = graphMeta[subNodeName]
      if (!subNode) { continue }

      getViewer(subNav, subNode, [...queryPath, subNav.edge])
    }
  }

  return result
})

const selectedRows = ref(new Set<string>())

watch(selectedRows, () => {

  if (nodeViews.value.length === 0 || selectedRows.value.size === 0) {
    return
  }

  const rowKey = selectedRows.value.values().next().value
  const row = provider.rows.value.find(r => r.key === rowKey) as DataRow | undefined

  if (!row) {
    return
  }

  const children = context.getChildrenViews(context.currentView.value!.viewId)
  if (children.length === 1 && children[0].visible) {

    // Get the viewer based on current child view's path
    const childPath = children[0].path

    const viewer = nodeViews.value.find(r => r.path === childPath)

    if (viewer) {

      const state: Record<string, any> = {}
      if (viewer.path === children[0].path) {
        // Order viewer should stay on the same selected tab
        state.selectedTab = children[0].state.selectedTab
      }

      children[0].navigate(
        viewer.path,
        {
          query: {
            id: row.values[viewer.idIdx]
          },
          state
        }
      )
    }
  }
})

function onDefaultAction() {

  if (nodeViews.value.length === 0 || selectedRows.value.size === 0) {
    return
  }

  const rowKey = selectedRows.value.values().next().value
  const row = provider.rows.value.find(r => r.key === rowKey) as DataRow | undefined

  if (!row) {
    return
  }

  const viewer = nodeViews.value[0]

  const children = context.getChildrenViews(context.currentView.value!.viewId)
  if (children.length === 1) {
    children[0].navigate(
      viewer.path,
      {
        query: {
          id: row.values[viewer.idIdx]
        }
      }
    )
  } else {
    context.openView(
      viewer.path,
      {
        query: {
          id: row.values[viewer.idIdx]
        },
        parentViewId: context.currentView.value!.viewId
      }
    )
  }
}

const tableContextMenuProvider: DataTableContextMenuProvider = {
  forHeader(column: DataColumn) {

    return [[{
      label: 'Rename column',
      action() {
        dialogs.openModal(
          'Rename Column',
          ColumnRenameDialog,
          true,
          {
            provider,
            column
          }
        )
      }
    }]] as ContextMenuSections
  },
  forRow(row: DataRow) {

    const result = [] as ContextMenuSections

    if (nodeViews.value.length > 0) {
      const openSection = nodeViews.value.map(v => <ContextMenuItem>{
        label: 'Open ' + v.name,
        action() {

          const children = context.getChildrenViews(context.currentView.value!.viewId)

          if (children.length === 1) {
            children[0].navigate(
              v.path,
              {
                query: {
                  id: row.values[v.idIdx]
                }
              }
            )
          } else {
            context.openView(
              v.path,
              {
                query: {
                  id: row.values[v.idIdx]
                },
                parentViewId: context.currentView.value!.viewId
              }
            )
          }

        }
      }) as ContextMenuSection

      openSection[0].shortcut = 'Enter'

      result.push(openSection)
    }

    return result
  }
}

const tableContextMenu = computed(() => {

  const saveGroup: ContextMenuSection = [] as ContextMenuSection

  if (context.currentView.value!.hasPendingChanges.value) {
    saveGroup.push({
      label: 'Save',
      action: save
    })
  }

  return [saveGroup, [{
    label: 'Refresh',
    action: refresh
  }, {
    label: 'Copy query JSON request',
    hidden: true,
    action: () => {
      const rootEdges = Object.keys(graphMeta).reduce((res, nodeKey) => {
        const node = graphMeta[nodeKey];
        res[node.rootEdgeName] = {
          node: nodeKey,
          cardinality: 'many'
        };
        return res;
      }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>);

      const builtQuery = buildQuery(query, context.currentView.value!.state.selections, graphMeta, query.navigation, rootEdges, false);
      if (builtQuery) {
        navigator.clipboard.writeText(JSON.stringify([builtQuery], null, 2));
      }
    }
  }], [{
    label: 'Download CSV',
    action: () => exportTable('csv')
  }, {
    label: 'Download Excel',
    action: () => exportTable('xlsx')
  }]]
})

/**
 * Creates a map of the query's actual columns. This function mirrors the order of use-query-builder in combination
 * with server-side table formatting to create an easy-to-use map for finding primary keys in the result set.
 * @param nav
 * @param edges
 * @param searchedColumns
 * @param currentPath
 */
function updateFieldIndex(
  nav: QueryNavigation,
  edges: Record<string, Models.GraphMeta.GraphEdgeMeta>,
  searchedColumns: Record<string, number>,
  currentPath: string[] = [],
) {
  if (!nav.edge) {
    return
  }

  const currentEdge = edges[nav.edge]

  if (!currentEdge) {
    return
  }

  const currentNode = graphMeta[currentEdge.node]

  currentPath.push(nav.edge)

  const selectedColumns = query.columns.filter(r => r.queryPath.join("@") === currentPath!.join("@"))

  if (!selectedColumns.find(r => r.fieldName === currentNode.idField)) {
    searchedColumns[currentPath.join('.') + '@' + currentNode.idField] = Object.keys(searchedColumns).length
  }

  for (const selectedColumn of selectedColumns) {
    let fieldName = selectedColumn.fieldName
    const period = fieldName.indexOf('.')
    if (period > -1) {
      fieldName = fieldName.substring(0, period)
    }

    const key = currentPath.join('.') + '@' + fieldName

    if (!searchedColumns[key]) {
      searchedColumns[key] = Object.keys(searchedColumns).length
    }
  }

  if (!nav.navigation) {
    return
  }

  for (const subNav of nav.navigation) {
    updateFieldIndex(
      subNav,
      currentNode.edges,
      searchedColumns,
      currentPath
    )
  }
}

</script>

<template>
  <div
    v-if="Object.keys(graphMeta).length > 0"
    class="query-viewer"
    :class="{
      'query-viewer--editor-open': context.currentView.value!.state.showEditor
    }"
  >
    <Panel class="query-viewer__editor-panel">
      
      <QueryEditor v-model="query" class="query-viewer__editor" />
      <div class="query-viewer__editor-error" v-if="queryError">
        {{ queryError }}
      </div>
    </Panel>

    <div
      class="query-table"
      :class="{ 'query-table--with-warnings': provider.warnings.value.length > 0 }"
      v-context-menu="tableContextMenu"
    >
      <div class="toolbar">
        <div class="toolbar-section">
          <ToggleButton
            class="toolbar-button"
            v-model="context.currentView.value!.state.showEditor"
            title="Toggle data navigator"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><rect x="16" y="104" width="48" height="48" rx="8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><rect x="152" y="40" width="64" height="64" rx="8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><rect x="152" y="152" width="64" height="64" rx="8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="64" y1="128" x2="112" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><path d="M152,184H128a16,16,0,0,1-16-16V88a16,16,0,0,1,16-16h24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>
          </ToggleButton>
          <DropdownButton class="toolbar-button">
            <template #icon>
              <Icon name="download"/>
            </template>
            <template #default>
              <DropdownItem @click="() => exportTable('csv')">
                Download CSV
              </DropdownItem>
              <DropdownItem @click="() => exportTable('xlsx')">
                Download Excel
              </DropdownItem>
            </template>
          </DropdownButton>
          <div class="search-bar">
            <TextInput
              v-model="searchPhrase"
              placeholder="Search"
              type="search"
            />
          </div>
        </div>
        <div class="toolbar-section">
          <div class="btn-group">
            <button
              class="toolbar-button"
              title="Refresh"
              :disabled="!query.navigation.edge"
              @click="refresh"
            >
              <IconReload />
            </button>
            <ToggleButton
              class="auto-refresh toolbar-button"
              title="Toggle auto refresh"
              v-model="provider.autoRefresh.value"
              :disabled="!query.navigation.edge"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><path d="M72,39.88V216.12a8,8,0,0,0,12.15,6.69l144.08-88.12a7.82,7.82,0,0,0,0-13.38L84.15,33.19A8,8,0,0,0,72,39.88Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="12"/></svg>
            </ToggleButton>
          </div>
          
          <button
            class="btn-primary"
            @click="save"
            :disabled="!context.currentView.value!.hasPendingChanges.value"
          >
            {{ saveButtonLabel }}
          </button>
        </div>
      </div>
      <div
        class="query-warnings"
        v-if="provider.warnings.value.length > 0"
      >
        <div
          class="query-warnings__item"
          v-for="(warning, idx) in provider.warnings.value"
          :key="idx"
        >
          <Icon name="warning" />
          <span>{{ warning.message }}</span>
        </div>
      </div>
      <DataTable
        :provider="provider"
        :context-menu-provider="tableContextMenuProvider"
        v-model:selected="selectedRows"
        @default-action="onDefaultAction"
      />
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  justify-content: space-between;
}

.toolbar-section {
  display: flex;
  gap: 5px;
  align-items: center;
}

.toolbar-button {
  padding: 0;
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.toolbar-button .icon),
.toolbar-button svg {
  width: 17px;
  height: 17px;
}

.query-table {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: min-content 1fr;
}

.query-table--with-warnings {
  grid-template-rows: min-content min-content 1fr;
}

.query-warnings {
  padding: 0.5rem 1rem;
  background-color: var(--color-warning-100);
  border-bottom: 1px solid var(--color-warning);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.query-warnings__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.query-warnings__item svg {
  width: 26px;
  height: 26px;
}




@keyframes button-pulse {
  0% {
    outline: 1px solid hsl(var(--color-primary));
  }

  100% {
    outline: 1px solid hsla(var(--color-primary), 0);
  }
}
</style>

<style>
.query-viewer {
  height: 100%;
  width: 100%;
  position: relative;
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 100%;
}

.query-viewer--editor-open {
  grid-template-columns: auto minmax(calc(100% - 500px), 70%);
}

.query-viewer__editor-error {
  padding: 1rem;
  color: var(--color-invert);
  background-color: var(--color-error-100);
  border-top: 1px solid var(--color-error);
}

.query-viewer__editor-panel {
  display: none;
  overflow: auto;
  padding: 0;
}

.query-viewer--editor-open .query-viewer__editor-panel {
  display: grid;
  grid-template-rows: min-content min-content 1fr;
  grid-auto-rows: min-content;
}


.query-viewer__editor {
  display: contents;
}

.query-viewer__editor .query-editor__margin {
  overflow: auto;
}
</style>